import { TrashIcon } from '@heroicons/react/20/solid'
import { useCallback } from 'react'
import TextareaAutosize from 'react-textarea-autosize'

import { eventBus } from '../../../services/event-bus/eventBus.service'
import { OpenDeleteSnippetModal } from '../../../services/event-bus/events'
import { Snippet } from '../../../services/models/Snippet.model'
import { Button } from '../../UI/Button'
import { Input } from '../../UI/Input'
import { useForm } from './hooks/useForm'

interface Props {
    snippet?: Snippet
    onClose?: () => void
}

/*
 * Component for creating and editing snippets
 * Handles both new snippet creation and existing snippet editing
 */
export const SnippetDetail = ({ snippet, onClose }: Props) => {
    const snippetId = snippet?.id

    // Initialize form with existing snippet data if editing
    const { form, isLoading, updateField, validateField, submit } = useForm(snippet ? { id: snippet.id, abbreviation: snippet.abbreviation, content: snippet.content } : undefined)

    /**
     * Handle form submission
     * Validates and submits form data, then closes the form on success
     */
    const handleSubmit = useCallback(async () => {
        submit(() => {
            if (onClose) onClose()
        })
    }, [submit, onClose])

    // In your handleDelete function
    const handleDelete = useCallback(() => {
        if (!snippetId) return

        eventBus.emit({
            action: OpenDeleteSnippetModal.action,
            payload: {
                snippetId,
                snippetName: form.abbreviation.value as string,
                onSuccess: onClose
            }
        })
    }, [snippetId, form.abbreviation.value, onClose])

    return (
        <>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-medium">{snippetId ? 'Edit Snippet' : 'New Snippet'}</h2>
                {snippetId && <Button intent="secondary" label="Delete" leftIconNode={<TrashIcon className="w-5 h-5" />} onClick={handleDelete} isLoading={isLoading} />}
            </div>
            <form
                onSubmit={(e) => {
                    e.preventDefault()
                    handleSubmit()
                }}
            >
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Abbreviation</label>
                    <Input
                        name="Abbreviation"
                        initialValue={form.abbreviation.value as string}
                        onChange={(e) => updateField('abbreviation', e.toLowerCase())}
                        onBlur={() => validateField('abbreviation')}
                        placeholder="/example"
                    />
                    {form.abbreviation.error && <p className="mt-1 text-sm text-rose-700">{form.abbreviation.error}</p>}
                </div>
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
                    <TextareaAutosize
                        name="Text"
                        className=" w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:leading-6"
                        defaultValue={form.content.value as string}
                        minRows={7}
                        onChange={(e) => updateField('content', e.target.value)}
                        onBlur={() => validateField('content')}
                        placeholder="Enter snippet content..."
                    />
                    {form.content.error && <p className="mt-1 text-sm text-rose-700">{form.content.error}</p>}
                </div>
                <div className="flex justify-end space-x-3">
                    <Button intent="primary" label={snippetId ? 'Update' : 'Create Snippet'} isLoading={isLoading} />
                </div>
            </form>
        </>
    )
}
